import { useEffect, useState } from "react";
import { normalizeMarket, type Market } from "../contest/api";
import type { ProducerContest } from "./api";

// Markets of the producer's live contests, by slug. A contest is here only
// when its market read came back; one that failed keeps its Back Me link-out.
export type CoSignMarkets = Record<string, Market>;

export function marketURL(slug: string): string {
  return `/v1/web/contests/${encodeURIComponent(slug)}/market`;
}

// coSignableSlugs is the live contests worth a market read: the ones whose
// line on the producer payload is open.
export function coSignableSlugs(live: ProducerContest[]): string[] {
  return live.filter((c) => c.market?.status === "open").map((c) => c.slug);
}

// Finds this producer's outcome in a market. The producer payload carries no
// id, so the match is on the username the market read sent.
export function outcomeOf(market: Market, username: string) {
  const want = username.toLowerCase();
  return market.outcomes.find((o) => o.username.toLowerCase() === want);
}

// useCoSignMarkets reads the market of every open live contest, as the
// session. The page needs the viewer's own can_back before it offers a
// co-sign, so it never shows a button PlaceStake would refuse.
export function useCoSignMarkets(live: ProducerContest[]): {
  markets: CoSignMarkets;
  onMarket: (slug: string, market: Market) => void;
} {
  const slugs = coSignableSlugs(live);
  const key = slugs.join(",");
  const [markets, setMarkets] = useState<CoSignMarkets>({});

  useEffect(() => {
    if (key === "") {
      setMarkets({});
      return;
    }
    const ctrl = new AbortController();
    let current = true;
    Promise.all(
      key.split(",").map(async (slug): Promise<[string, Market | null]> => {
        try {
          const res = await fetch(marketURL(slug), {
            method: "GET",
            credentials: "same-origin",
            signal: ctrl.signal,
            headers: { Accept: "application/json" },
          });
          if (!res.ok) return [slug, null];
          return [slug, normalizeMarket(await res.json())];
        } catch {
          return [slug, null];
        }
      }),
    ).then((pairs) => {
      if (!current) return;
      const next: CoSignMarkets = {};
      for (const [slug, market] of pairs) if (market) next[slug] = market;
      setMarkets(next);
    });
    return () => {
      current = false;
      ctrl.abort();
    };
  }, [key]);

  const onMarket = (slug: string, market: Market) => setMarkets((prev) => ({ ...prev, [slug]: market }));
  return { markets, onMarket };
}
